import React, { useCallback, useEffect, useState } from 'react';
import { getMyCalendarFeeds } from '../../../services/calendarFeedService';
import { getLocationColor } from './locations';

// Subscribe tab — self-service calendar-feed links for staff.
//
// WHY TWO FEEDS AND NOT ONE. Neither Apple Calendar nor Google honors a
// per-EVENT color on a subscribed calendar; color is a property of the
// CALENDAR. So the only way to see Lakeville and Headline in distinct colors is
// two subscriptions, coloured separately in the calendar app. Together they are
// the whole studio — nothing is in neither.
//
// WHY THIS LIVES ON SCHEDULING AND NOT THE ADMIN PANEL. AdminPanel gates on
// `role === 'admin'`, and Annelise is `employee` — putting it there would have
// locked out one of the three people who need it. Scheduling is
// employee-or-admin, which is exactly the audience.
//
// Each person gets THEIR OWN links; the callable is scoped to the caller's uid
// and takes no argument that widens it. Grace opens this page and gets hers.

const SANS = "'Josefin Sans', system-ui, -apple-system, sans-serif";
const SERIF = "'Josefin Slab', Georgia, serif";

// The feed key → the location it carries, so the card can wear the same accent
// the Calendar grid uses. Keyed off the shared LOCATIONS palette rather than a
// second copy of the hexes.
const FEED_LOCATION = { lakeville: 'Lakeville', headline: 'HLDC' };

const SubscribeTab = () => {
	const [feeds, setFeeds] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [copied, setCopied] = useState('');
	const [rotating, setRotating] = useState(false);

	const load = useCallback(async (opts) => {
		setLoading(true);
		setError('');
		try {
			setFeeds(await getMyCalendarFeeds(opts));
		} catch (e) {
			console.error('Error loading calendar feeds:', e);
			setError('Couldn’t load your subscription links. Please try again.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { load(); }, [load]);

	const copy = async (feedKey, url) => {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(feedKey);
			setTimeout(() => setCopied((c) => (c === feedKey ? '' : c)), 2500);
		} catch (e) {
			console.error('Clipboard write failed:', e);
			setError('Couldn’t copy — select the link and copy it manually.');
		}
	};

	const rotate = async () => {
		if (!window.confirm(
			'Replace your subscription links with new ones?\n\n' +
			'Anything already subscribed to your old links will silently stop updating, ' +
			'and you’ll need to re-subscribe. Do this if a link leaked.'
		)) return;
		setRotating(true);
		await load({ rotate: true });
		setRotating(false);
	};

	const card = (feed) => {
		const c = getLocationColor(FEED_LOCATION[feed.feedKey] || 'Lakeville');
		const isCopied = copied === feed.feedKey;
		return (
			<div key={feed.feedKey} style={{
				border: '1px solid #ece7e0', borderLeft: '5px solid ' + c.bar, borderRadius: 13,
				background: '#fff', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12,
			}}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
					<span style={{ width: 11, height: 11, borderRadius: '50%', background: c.dot, flex: '0 0 auto' }} />
					<span style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 600, color: '#384971' }}>{feed.calName}</span>
				</div>
				<div style={{ fontSize: '12.5px', color: '#7b8499', lineHeight: 1.5 }}>
					{feed.feedKey === 'lakeville'
						? 'Lakeville lessons and events — plus recitals and anything off-site, so they appear exactly once across your two calendars.'
						: 'Headline (HLDC) lessons, events, and booked trial lessons.'}
				</div>
				<div style={{
					fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11,
					background: '#faf8f5', border: '1px solid #f0ece6', borderRadius: 8,
					padding: '9px 11px', color: '#8a93a4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
				}}>
					{/* Masked. The URL is a credential and this is a shared-office screen. */}
					webcal://…/calendarFeed/••••••••••••.ics
				</div>
				<div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
					<a href={feed.webcalUrl} style={{
						appearance: 'none', height: 38, padding: '0 16px', borderRadius: 10, border: 'none',
						background: c.bar, color: '#fff', fontFamily: SANS, fontSize: '13.5px', fontWeight: 600,
						display: 'inline-flex', alignItems: 'center', gap: 7, textDecoration: 'none',
					}}>
						<i className="fa-sharp fa-solid fa-calendar-plus" />Subscribe
					</a>
					<button type="button" onClick={() => copy(feed.feedKey, feed.webcalUrl)} style={{
						appearance: 'none', height: 38, padding: '0 16px', borderRadius: 10,
						border: '1px solid #e3ddd4', background: '#fff', color: '#384971',
						fontFamily: SANS, fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
						display: 'inline-flex', alignItems: 'center', gap: 7,
					}}>
						<i className={`fa-sharp fa-solid ${isCopied ? 'fa-check' : 'fa-copy'}`} />
						{isCopied ? 'Copied' : 'Copy link'}
					</button>
				</div>
			</div>
		);
	};

	return (
		<div style={{ maxWidth: 760, fontFamily: SANS }}>
			<div style={{ marginBottom: 18 }}>
				<div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 600, color: '#384971', marginBottom: 6 }}>
					Subscribe to the studio calendar
				</div>
				<div style={{ fontSize: '13.5px', color: '#7b8499', lineHeight: 1.6 }}>
					Two feeds, one per location, so you can colour them apart on your phone —
					calendar apps colour a whole calendar, never a single event. These links are
					yours; anyone else opens this page to get their own.
				</div>
			</div>

			{loading && <div style={{ color: '#a6adba', padding: '32px 0' }}>Loading…</div>}
			{error && (
				<div style={{
					background: '#fdf3f0', border: '1px solid #f0c9bc', color: '#c0532c',
					borderRadius: 10, padding: '11px 14px', fontSize: '13px', marginBottom: 14,
				}}>{error}</div>
			)}

			{!loading && feeds.length > 0 && (
				<div style={{ display: 'grid', gap: 14 }}>{feeds.map(card)}</div>
			)}

			{!loading && (
				<div style={{
					marginTop: 20, padding: '14px 16px', background: '#fcfaf7',
					border: '1px solid #f0ece6', borderRadius: 11, fontSize: '12.5px',
					color: '#7b8499', lineHeight: 1.6,
				}}>
					<strong style={{ color: '#384971' }}>Treat these links like passwords.</strong>{' '}
					A subscribed calendar can’t sign in, so the link itself is the credential — and it
					carries family names, lesson times, and trial families’ contact details. Send one
					through a password manager, never in an email subject.
					<div style={{ marginTop: 10 }}>
						On a Mac, choose <strong>iCloud</strong> as the location so it reaches your phone
						too, and set <strong>Auto-refresh: every hour</strong>. Google refreshes on its own
						schedule — roughly daily — and that isn’t adjustable.
					</div>
					<button type="button" onClick={rotate} disabled={rotating} style={{
						marginTop: 12, appearance: 'none', height: 34, padding: '0 14px', borderRadius: 9,
						border: '1px solid #f0c9bc', background: '#fff', color: '#c0532c',
						fontFamily: SANS, fontSize: '12.5px', fontWeight: 600,
						cursor: rotating ? 'not-allowed' : 'pointer', opacity: rotating ? 0.6 : 1,
					}}>
						{rotating ? 'Replacing…' : 'Replace my links (if one leaked)'}
					</button>
				</div>
			)}
		</div>
	);
};

export default SubscribeTab;
